import React, { useState, useEffect } from "react"
import { useQuery } from "react-query"
import {
  fetchOpenPositionsByDays,
  fetchAllOpenPositionsByDaysForCSV
} from "../../services/main.service"
import Paginator from "../composites/paginator"
import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Grid,
  Button,
  Input,
  IconButton,
  Select,
  useToast
} from "@chakra-ui/react"
import _ from "lodash"
import BlockedLoader from "../composites/blocked-loader"
import DateRangePicker from "../composites/date-range-picker"
import { AiOutlineSearch, AiOutlineDownload } from "react-icons/ai"
import { InputWrapper } from "../composites"
import { SelectSymbols } from "../composites/select-symbols"
import { formatDate } from "../../utils"

const captions = [
  "Deal",
  "Position",
  "Login",
  "Name",
  "Time",
  "Entry",
  "Symbol",
  "Volume",
  "Price",
  "S/L",
  "T/P",
  "Reason",
  "Profit",
  "Dealer",
  "Comment",
]

// Define the type for the deal array items
type DealItemArray = string | number | null;

interface FinalState {
  year: string;
  loginId: string;
  symbolStr: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  days: number;
  positionId: string;
}


function convertJsonToCsv(jsonArray: Record<string, any>[]) {
  if (!jsonArray || jsonArray.length === 0) {
    return '';
  }
  
  const headers = Object.keys(jsonArray[0]);
  
  // Create the CSV header row
  const csvHeader = headers.join(',');
  
  // Create the CSV data rows with optimized processing
  const csvRows = jsonArray.map(obj => {
    return headers
      .map(header => {
        const value = obj[header];
        
        // Handle null/undefined values
        if (value === null || value === undefined) {
          return '';
        }
        
        // Special handling for the Time column to ensure proper Excel formatting
        if (header === 'Time' && value) {
          return `"${value}"`;
        }
        
        // If the value contains commas, quotes, or newlines, wrap it in double quotes
        // and escape any existing quotes
        if (typeof value === 'string' && 
            (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes(' '))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      })
      .join(',');
  });
  
  // Combine the header and rows to form the complete CSV string
  return [csvHeader, ...csvRows].join('\n');
}

interface DownloadFileParams {
  data: string;
  fileName: string;
  fileType: string;
}


const downloadFile = ({ data, fileName, fileType }: DownloadFileParams) => {
  const blob = new Blob([data], { type: fileType });
  const a = document.createElement('a');
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  document.body.appendChild(a); 
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(a.href); 
}


const downloadOpenPositionsByDaysToCSV = (apiResponse: any, columns: string[], fileName: string) => {

  if (!apiResponse?.data?.deals || apiResponse.data.deals.length === 0) {
    console.warn("No data available for download");
    return;
  }
  const batchSize = 500;
  const deals = apiResponse.data.deals;
  const totalDeals = deals.length;
  let processedData: Record<string, any>[] = [];
  
  // Map API field keys to readable column names
  const fieldToColumnMap: Record<string, string> = {
    'd': 'Deal',
    'pid': 'Position',
    'l': 'Login',
    't': 'Time',
    'TimeCreate': 'Time', 
    'e': 'Entry',
    's': 'Symbol',
    'v': 'Volume',
    'p': 'Price',
    'sl': 'S/L',
    'tp': 'T/P',
    'r': 'Reason',
    'pr': 'Profit',
    'de': 'Dealer',
    'co': 'Comment'
  };
  
  // Process data in batches using setTimeout to keep UI responsive
  const processBatch = (startIndex: number) => {
    const endIndex = Math.min(startIndex + batchSize, totalDeals);
    
    for (let i = startIndex; i < endIndex; i++) {
      const deal = deals[i];
      const formattedDeal: Record<string, any> = {};
      
      // Format each column according to the display requirements
      columns.forEach(column => {
        if (column === 'Name') {
          // Special handling for Name which combines first, last and middle names
          formattedDeal[column] = `${deal.firstn || ''} ${deal.lastn || ''} ${deal.middlen || ''}`.trim();
        } else {
          // Map API field to column name using our lookup table
          const apiField = Object.keys(fieldToColumnMap).find(
            key => fieldToColumnMap[key] === column
          );
          
          if (apiField && deal[apiField] !== undefined) {
            formattedDeal[column] = deal[apiField];
          } else if (column === 'Time' && deal['TimeCreate'] !== undefined) {
            // Special case for Time field which might be in TimeCreate
            formattedDeal[column] = deal['TimeCreate'];
          } else {
            formattedDeal[column] = '';
          }
        }
      });
      
      processedData.push(formattedDeal);
    }
    
    // If there's more data to process, schedule next batch
    if (endIndex < totalDeals) {
      setTimeout(() => processBatch(endIndex), 0);
    } else {
      // All data processed, generate and download CSV
      const csvData = convertJsonToCsv(processedData);
      downloadFile({
        data: csvData,
        fileName: `${fileName}.csv`,
        fileType: 'text/csv'
      });
    }
  };
  
  // Start processing the first batch
  processBatch(0);
}

const OpenPositionsByDaysPage = () => {
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [year, setyear] = useState("2023")
  const [currentPage, setCurrentpage] = useState(1)
  const [days, setDays] = useState(1)
  const [isDownloading, setIsDownloading] = useState(false)
  const toast = useToast()

  const [[startDate, endDate], setDateRange] = useState<[Date | undefined, Date | undefined]>([undefined, undefined])

  const [loginId, setLoginId] = useState("")
  const [positionId, setPositionId] = useState("")
  const [symbolStr, setSymbolStr] = useState("")
  const [finalState, setFinalState] = useState<FinalState>({
    year: year,
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
    days: days,
    positionId: positionId,
  })

  const updateFinalState = () => {
    setFinalState({
      year: year,
      loginId,
      symbolStr,
      startDate,
      endDate,
      days,
      positionId,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
    refetch,
  } = useQuery(
    [
      "open-positions-by-days",
      {
        currentPage,
        finalState,
      },
    ],
    () =>
      fetchOpenPositionsByDays(
        finalState.days,
        finalState.year,
        currentPage,
        finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.endDate
          ? formatDate(finalState?.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr,
        finalState.positionId
      ),
    {
      refetchInterval: 15000,
    }
  )

  useEffect(() => {
    if (isFetchedAfterMount) {
      setIsForcedUpdate(true)
    }
  }, [currentPage, year])

  useEffect(() => {
    if (!isFetchedAfterMount) {
      if (!isRefetching) {
        if (isSetForcedUpdate) {
          setIsForcedUpdate(false)
        }
      }
    }
  }, [isRefetching])

  // Optimized download function that fetches all data at once
  const downloadCSV = async () => {
    try {
      setIsDownloading(true);
      
      // Fetch all data for CSV with the current filter parameters
      const response = await fetchAllOpenPositionsByDaysForCSV(
        finalState.days,
        finalState.year,
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr,
        finalState.positionId
      );
      
      // Check if we have deals data
      if (!response?.data?.deals || response.data.deals.length === 0) {
        toast({
          title: "No data available",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        setIsDownloading(false);
        return;
      }
      
      downloadOpenPositionsByDaysToCSV(
        response,
        captions,
        `open-positions-days-${finalState.days}-${formatDate(finalState.startDate || new Date())}`
      );
      
      toast({
        title: "Download successful",
        description: `${response.data.deals.length} records downloaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast({
        title: "Download failed",
        description: "There was an error downloading the CSV file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Grid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Enter Login ID">
            <Input
              value={loginId}
              onChange={(e) => setLoginId(e?.target?.value)}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Enter Position ID">
            <Input
              value={positionId}
              onChange={(e) => setPositionId(e?.target?.value)}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <SelectSymbols
            selectedSymbol={symbolStr ?? "ALL"}
            onOptionChange={(option: string) => {
              setSymbolStr(option)
            }}
          />
        </Box>

        <Box width="100%" mr={6} my={4}>
          <InputWrapper title="Select no of days">
            <Select
              value={days}
              onChange={(e) => {
                setDays(parseInt(e?.target?.value))
              }}
            >
              <option value={1}>more than 1 day</option>
              <option value={2}>more than 2 days</option>
              <option value={7}>more than 7 days</option>
              <option value={15}>more than 15 days</option>
              <option value={30}>more than 30 days</option>
            </Select>
          </InputWrapper>
        </Box>
      </Grid>

      <Flex justify="space-between" align="center" mb={4}>
        <Box>
          <Button
            colorScheme="blue"
            onClick={() => {
              updateFinalState()
            }}
            leftIcon={<AiOutlineSearch />}
          >
            Search
          </Button>
        </Box>
        <Box>
          <Button 
            colorScheme="green" 
            leftIcon={<AiOutlineDownload />}
            isDisabled={isLoading || isDownloading}
            isLoading={isDownloading}
            loadingText="Downloading..."
            onClick={downloadCSV}
          >
            Download
          </Button>
        </Box>
      </Flex>

      <DealsGrid>
        {isLoading ? (
          <Tr>
            <Td colSpan={captions?.length}>
              <BlockedLoader isBlocked={isLoading}>
                <></>
              </BlockedLoader>
            </Td>
          </Tr>
        ) : (
          <>
            {data?.deals?.data?.length ? (
              <>
                {_.map(data?.deals?.data ?? [], (deal) => {
                  return <DealsTableRow key={deal} deal={deal} />
                })}
              </>
            ) : (
              <>
                <Tr>
                  <Td colSpan={captions?.length}>
                    <Box p={4} textAlign="center">
                      <Text color="text.regular">
                        No data found for the selected criteria
                      </Text>
                    </Box>
                  </Td>
                </Tr>
              </>
            )}
          </>
        )}
      </DealsGrid>

      <Box my={4}>
        <Paginator
          currentPage={currentPage}
          totalPages={data?.deals?.last_page ?? 1}
          onChangePage={(p: number) => setCurrentpage(p)}
        />
      </Box>
    </>
  )
}

export default OpenPositionsByDaysPage

interface DealsGridProps {
  children: React.ReactNode;
}

const DealsGrid = ({ children }: DealsGridProps) => {
  return (
    <>
      <Box overflowX="auto">
        <Table variant="striped" color="text.regular" border={1}>
          <Thead>
            <Tr my=".8rem" ps="0px">
              {captions.map((caption, idx) => {
                return (
                  <Th
                    color="gray.400"
                    key={idx}
                    ps={idx === 0 ? "0px" : "0px"}
                    textAlign={caption === "PL" ? "right" : "unset"}
                  >
                    {caption}
                  </Th>
                )
              })}
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
      </Box>
    </>
  )
}

interface DealsTableRowProps {
  deal: any[];
}

const DealsTableRow = ({ deal }: DealsTableRowProps) => {
  return (
    <>
      <Tr>
        {_.map(deal, (_d, index) => {
          if (deal?.length === index + 1) return <></> 

          return (
            <Td pl={1} pr={2} key={index}>
              <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                <Text fontSize="md" fontWeight="bold" minWidth="100%">
                  {_d}
                </Text>
              </Flex>
            </Td>
          )
        })}
      </Tr>
    </>
  )
}